// Initial-locale detection.
//
// English is the PRIMARY system language (product decision 2026-06-21): every
// first visit renders in English regardless of browser language. Other
// locales are opt-in only — via the LanguageSwitcher (persisted below) or the
// DB-stored preference applied after login.
//
// Priority (highest first):
//   1. localStorage `ohear-locale` — last value the user picked in this browser.
//      The DB-stored preference, when known, is applied AFTER login by
//      `LocaleProvider` so a returning user's saved choice still wins on a new
//      device, but local-only changes survive sign-out.
//   2. Default ('en'). Deliberately NO navigator.language sniffing — a zh/es
//      browser must still land on English until the user explicitly switches.
//
// SSR-safe: `localStorage` is guarded so this module can be imported during
// build / pre-render without crashing.

import { DEFAULT_LOCALE, type Locale, isLocale } from '@ohear/shared'

/** localStorage key — also referenced by `locale-context.tsx`. */
export const LOCALE_STORAGE_KEY = 'ohear-locale'

/** Read the persisted locale, if any. Returns null when none is set. */
export function readStoredLocale(): Locale | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(raw) ? raw : null
  } catch {
    // localStorage can throw in private mode / quota-exceeded — never fatal.
    return null
  }
}

/** Persist the user's locale. Silently no-ops when storage is unavailable. */
export function writeStoredLocale(locale: Locale): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // ignore — best-effort persistence
  }
}

/**
 * Resolve the locale to use on first paint. Cheap and synchronous so it can be
 * called inside `useState(initial)` without blocking render.
 *
 * English-first: stored explicit choice wins; otherwise ALWAYS the default
 * ('en'). No browser-language sniffing by design.
 */
export function detectInitialLocale(): Locale {
  return readStoredLocale() ?? DEFAULT_LOCALE
}
